/**
 * Confirm Dialog
 *
 * A modal dialog for confirmation actions, replacing native browser confirm().
 * Features smooth enter/exit animations.
 */

import { cn } from '@/lib/utils';
import { Button } from './button';
import { X } from 'lucide-react';
import { useAnimatedMount, ANIMATION_DURATION } from '@/hooks/useAnimatedMount';
import { createPortal } from 'react-dom';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  isLoading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  isLoading = false,
}: ConfirmDialogProps) {
  const { shouldRender, isVisible } = useAnimatedMount(isOpen, {
    exitDuration: ANIMATION_DURATION.normal,
    enterDuration: ANIMATION_DURATION.normal,
  });

  if (!shouldRender) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-black/50',
          'transition-opacity duration-150',
          isVisible ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        className={cn(
          'relative bg-white dark:bg-surface-900 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden',
          'transition-all duration-150',
          isVisible
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-[0.98] translate-y-1'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
          <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-surface-600 dark:text-surface-400">
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 px-6 py-4 bg-surface-50 dark:bg-surface-800">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={isLoading}
            className={cn(
              variant === 'destructive' && 'bg-red-600 hover:bg-red-700 text-white'
            )}
          >
            {isLoading ? 'Please wait...' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
